import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
    return twMerge(clsx(inputs));
}

// Format date to German locale
export function formatDate(dateString, options = {}) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        ...options,
    });
}

// Format date with time
export function formatDateTime(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('de-DE', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
}

// Format relative time
export function formatRelativeTime(dateString) {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);
    
    if (minutes < 1) return 'Gerade eben';
    if (minutes < 60) return `vor ${minutes} Min.`;
    if (hours < 24) return `vor ${hours} Std.`;
    if (days < 7) return `vor ${days} Tagen`;
    
    return formatDate(dateString);
}

// Ticket status labels (German)
export const ticketStatusLabels = {
    open: 'Offen',
    in_progress: 'In Bearbeitung',
    waiting_customer: 'Warte auf Kunde',
    resolved: 'Gelöst',
    closed: 'Geschlossen',
};

// Ticket category labels (German)
export const ticketCategoryLabels = {
    project_request: 'Projektanfrage',
    seo_audit: 'SEO-Audit',
    support: 'Support',
    general: 'Allgemein',
};

// Ticket priority labels (German)
export const ticketPriorityLabels = {
    low: 'Niedrig',
    medium: 'Mittel',
    high: 'Hoch',
    urgent: 'Dringend',
};

// Software dev status labels (German)
export const softwareStatusLabels = {
    planning: 'In Planung',
    in_development: 'In Entwicklung',
    beta: 'Beta',
    released: 'Veröffentlicht',
    maintenance: 'Wartung',
};

// User role labels (German)
export const userRoleLabels = {
    admin: 'Administrator',
    support: 'Support',
    customer: 'Kunde',
};

// Truncate text
export function truncate(text, length = 100) {
    if (!text) return '';
    if (text.length <= length) return text;
    return text.substring(0, length) + '...';
}

// Generate initials from name
export function getInitials(name) {
    if (!name) return '??';
    return name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .substring(0, 2);
}

// Slugify text
export function slugify(text) {
    return text
        .toLowerCase()
        .replace(/ä/g, 'ae')
        .replace(/ö/g, 'oe')
        .replace(/ü/g, 'ue')
        .replace(/ß/g, 'ss')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/(^-|-$)/g, '');
}

// Validate email
export function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// Format file size
export function formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

// Get platform icon name
export function getPlatformIcon(platform) {
    const icons = {
        windows: 'Monitor',
        macos: 'Apple',
        linux: 'Terminal',
        web: 'Globe',
        ios: 'Smartphone',
        android: 'Smartphone',
    };
    return icons[platform] || 'Package';
}

// Parse query params
export function parseQueryParams(search) {
    const params = new URLSearchParams(search);
    const result = {};
    for (const [key, value] of params) {
        result[key] = value;
    }
    return result;
}

// Build query string
export function buildQueryString(params) {
    const filtered = Object.entries(params).filter(([_, v]) => v != null && v !== '');
    if (filtered.length === 0) return '';
    return '?' + new URLSearchParams(filtered).toString();
}
